import { useState, useCallback, useRef, useEffect } from "react"
import type { BleState, BleConnectionStatus, SensorReading } from "@/types/device"
import {
  BLE_SERVICE_UUID,
  BLE_CHARACTERISTICS,
  TILT_DEVICE_NAME_PREFIX,
  isWebBluetoothSupported,
} from "@/lib/ble/constants"
import {
  parseSgReading,
  parseAccelerometer,
  parsePth,
  parseBatteryVoltage,
} from "@/lib/ble/parsers"

interface UseBleDeviceReturn {
  state: BleState
  reading: SensorReading
  scan: () => Promise<void>
  disconnect: () => void
  isSupported: boolean
}

export function useBleDevice(): UseBleDeviceReturn {
  const [state, setState] = useState<BleState>({
    status: "disconnected",
    error: null,
    device: null,
    server: null,
  })

  const [reading, setReading] = useState<SensorReading>({
    specificGravity: null,
    temperature: null,
    tiltAngle: null,
    batteryVoltage: null,
    accelerometer: null,
    pth: null,
  })

  const characteristicsRef = useRef<Map<string, BluetoothRemoteGATTCharacteristic>>(
    new Map()
  )
  const serviceRef = useRef<BluetoothRemoteGATTService | null>(null)

  const isSupported = isWebBluetoothSupported()

  // Handle device disconnection
  useEffect(() => {
    const device = state.device
    if (!device) return

    const handleDisconnect = () => {
      setState((prev) => ({
        ...prev,
        status: "disconnected",
        server: null,
      }))
      characteristicsRef.current.clear()
      serviceRef.current = null
    }

    device.addEventListener("gattserverdisconnected", handleDisconnect)
    return () => {
      device.removeEventListener("gattserverdisconnected", handleDisconnect)
    }
  }, [state.device])

  const setStatus = useCallback((status: BleConnectionStatus, error?: string) => {
    setState((prev) => ({
      ...prev,
      status,
      error: error ?? null,
    }))
  }, [])

  // Characteristic handlers - defined before scan so they can be used during connection
  const handleSgReading = useCallback((event: Event) => {
    const target = event.target as BluetoothRemoteGATTCharacteristic
    const value = target.value
    if (!value) return

    const data = parseSgReading(value)
    setReading((prev) => ({
      ...prev,
      specificGravity: data.specificGravity,
      tiltAngle: data.tiltAngle,
      temperature: data.temperature,
    }))
  }, [])

  const handleAccelerometer = useCallback((event: Event) => {
    const target = event.target as BluetoothRemoteGATTCharacteristic
    const value = target.value
    if (!value) return

    const data = parseAccelerometer(value)
    setReading((prev) => ({
      ...prev,
      accelerometer: data,
    }))
  }, [])

  const handlePth = useCallback((event: Event) => {
    const target = event.target as BluetoothRemoteGATTCharacteristic
    const value = target.value
    if (!value) return

    const data = parsePth(value)
    setReading((prev) => ({
      ...prev,
      pth: data,
    }))
  }, [])

  const handleBattery = useCallback((event: Event) => {
    const target = event.target as BluetoothRemoteGATTCharacteristic
    const value = target.value
    if (!value) return

    const voltage = parseBatteryVoltage(value)
    setReading((prev) => ({
      ...prev,
      batteryVoltage: voltage,
    }))
  }, [])

  const scan = useCallback(async () => {
    if (!isSupported) {
      setStatus("error", "Web Bluetooth is not supported")
      return
    }

    try {
      setStatus("scanning")

      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: TILT_DEVICE_NAME_PREFIX },
          { services: [BLE_SERVICE_UUID] },
        ],
        optionalServices: [BLE_SERVICE_UUID],
      })

      setState((prev) => ({
        ...prev,
        device,
        status: "connecting",
      }))

      const server = await device.gatt?.connect()
      if (!server) {
        throw new Error("Failed to connect to GATT server")
      }

      const service = await server.getPrimaryService(BLE_SERVICE_UUID)
      serviceRef.current = service

      setState((prev) => ({
        ...prev,
        server,
        status: "connected",
        error: null,
      }))

      // Auto-subscribe to all characteristics in parallel
      const subscriptions = [
        { uuid: BLE_CHARACTERISTICS.SG_READING, handler: handleSgReading },
        { uuid: BLE_CHARACTERISTICS.ACCELEROMETER, handler: handleAccelerometer },
        { uuid: BLE_CHARACTERISTICS.PTH, handler: handlePth },
        { uuid: BLE_CHARACTERISTICS.BATTERY, handler: handleBattery },
      ]

      await Promise.allSettled(
        subscriptions.map(async ({ uuid, handler }) => {
          const char = await service.getCharacteristic(uuid)
          characteristicsRef.current.set(uuid, char)
          char.addEventListener("characteristicvaluechanged", handler)
          await char.startNotifications()
        })
      )
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to connect to device"
      // User cancelled the scan - not an error
      if (message.includes("User cancelled")) {
        setStatus("disconnected")
      } else {
        setStatus("error", message)
      }
    }
  }, [isSupported, setStatus, handleSgReading, handleAccelerometer, handlePth, handleBattery])

  const disconnect = useCallback(() => {
    if (state.device?.gatt?.connected) {
      state.device.gatt.disconnect()
    }
    setState({
      status: "disconnected",
      error: null,
      device: null,
      server: null,
    })
    characteristicsRef.current.clear()
    serviceRef.current = null
    setReading({
      specificGravity: null,
      temperature: null,
      tiltAngle: null,
      batteryVoltage: null,
      accelerometer: null,
      pth: null,
    })
  }, [state.device])

  return {
    state,
    reading,
    scan,
    disconnect,
    isSupported,
  }
}
